import React, { useEffect, VFC } from "react";
import styled from "styled-components";
import { QuestionAnswer } from "~/models/questionAnswer";

interface TocContentProps {
  answerTableOfContentHtml: QuestionAnswer["answerTableOfContentHtml"];
}

const TocContent: VFC<TocContentProps> = ({ answerTableOfContentHtml }) => {
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const options: IntersectionObserverInit = {
      rootMargin: "0px 0px -75% 0px",
      threshold: 1,
    };

    const links =
      document.querySelectorAll<HTMLAnchorElement>("#sidebar-toc a");

    links.forEach((link) => {
      // using document.getElementById because document.querySelector method uses CSS3 selectors for querying the DOM
      // and CSS3 doesn't support ID selectors that start with a digit
      const heading = document.getElementById(
        decodeURIComponent(link.hash).substring(1)
      );

      if (!heading) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          const entry = entries[0];
          const { isIntersecting } = entry;

          if (!isIntersecting) {
            return;
          }

          links.forEach((targetLink) => {
            if (targetLink.href === link.href) {
              targetLink.classList.add("is-active");
            } else {
              targetLink.classList.remove("is-active");
            }
          });
        },
        options
      );

      observer.observe(heading);
    });
  }, []);

  if (!answerTableOfContentHtml) {
    return null;
  }

  return (
    <TableOfContent
      id="sidebar-toc"
      dangerouslySetInnerHTML={{ __html: answerTableOfContentHtml }}
    />
  );
};

export const TableOfContentBase = styled.div`
  > ol {
    position: relative;

    &::before {
      background-color: var(--pri-bg-clr100);
      border-radius: 0 0 5px 5px;
      bottom: 8px;
      content: "";
      left: 5px;
      position: absolute;
      top: 14px;
      width: 2px;
    }
  }

  ol {
    padding-left: 20px;

    & ol {
      padding-left: 0;
    }
  }

  li {
    position: relative;
    font-weight: 700;
    color: var(--sub-txt-clr);

    & + li {
      margin-top: 8px;
    }

    & li {
      margin-top: 8px;
      color: var(--b-clr300);
      font-weight: 500;

      a {
        &::before {
          background-color: var(--box-bdr-clr);
          height: 8px;
          left: -18px;
          top: 8px;
          width: 8px;
        }
      }
    }
  }

  a {
    position: relative;
    display: block;
    color: currentColor;
    font-size: 14px;
    line-height: 22px;
    &:hover {
      color: var(--b-clr800);
    }

    &::before {
      background-color: var(--box-bdr-clr);
      border-radius: 9999px;
      border: 2px solid var(--body-bg-clr);
      box-sizing: border-box;
      content: "";
      display: block;
      height: 12px;
      left: -20px;
      position: absolute;
      top: 6px;
      width: 12px;
    }
  }
`;

const TableOfContent = styled(TableOfContentBase)`
  a {
    &.is-active {
      color: var(--b-clr900);

      &::before {
        border-color: var(--box-bdr-clr);
        background-color: var(--pri-clr500);
        height: 12px;
        left: -20px;
        top: 6px;
        width: 12px;
      }
    }
  }
`;

export default TocContent;
